import { Section, Row, LockedCard } from "../components/ui.jsx";
import { SITE, VERSE, LINKS } from "../data/site.js";
import { PASS_LABEL, LEVEL_LABEL, rankOf } from "../data/levels.js";
import SERIES from "../data/series/ship-happens.json";
import { GROUPME, LIBRARY } from "../data/library.js";
import { SERMON_PLAYLIST_ID } from "../data/worship.js";
import logo from "../assets/cllxtv-logo.png";
import NotifyHeart from "../components/NotifyHeart.jsx";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export default function Home({ go, onSignOut, me, userRank, firstName, unread }) {
  const level = me.level;

  // Someone whose email verified but who matched nobody in Planning Center.
  // They get the pass plus the four public sections, and an offer to connect.
  const unlinked = me.linked === false;

  return (
    <div className="above">
      <div className="pass-wrap">
        {/* Unlinked members get the pass as a plain card — the dashboard is not
            part of their view, so it should not look tappable. */}
        {(() => {
          const face = (
            <>
              <div className="hole" />
              <img className="plogo" src={logo} alt="" />
              <div className="eyebrow">{SITE.name} · The Room</div>
              {firstName && <div className="pname">{firstName}</div>}
              <div className="lvl display">{PASS_LABEL[level]}</div>
              <div className="meta">
                <span>{SITE.campus}</span>
                <span>2026</span>
              </div>
              <div className="stripe">{SITE.tagline}</div>
              {!unlinked && <div className="pass-hint">Tap for your dashboard →</div>}
            </>
          );
          if (unlinked) return <div className="pass">{face}</div>;
          return (
            <button
              className="pass pass-btn"
              onClick={() => go("dashboard")}
              aria-label={
                unread
                  ? `Open your personal dashboard, ${unread} unread`
                  : "Open your personal dashboard"
              }
            >
              {face}
              <NotifyHeart count={unread} />
            </button>
          );
        })()}
      </div>

      <div className="welcome">
        <h1 className="display">
          {firstName ? `${greeting()}, ${firstName}.` : `${greeting()}.`}
        </h1>
        <p className="view-note">
          {unlinked ? (
            <>
              You're signed in, but we haven't matched you to Planning Center yet — so
              this is the visitor view.
            </>
          ) : (
            <>
              You're seeing the <b>{LEVEL_LABEL[level]}</b> view — everything here is
              yours. Tap your pass anytime to open your personal dashboard.
            </>
          )}
        </p>
      </div>

      <div className="wrap">
        {unlinked && (
          <div className="connect-card">
            <div className="ceyebrow">One more step</div>
            <h3>Connect your account.</h3>
            <p>
              We couldn't find {me.email ? <b>{me.email}</b> : "your email"} in Planning
              Center. Sign in with Planning Center once and we'll link the two — your
              team, your serving schedule, and everything your role unlocks will appear
              here automatically.
            </p>
            <a className="cbtn" href="/api/auth/login?link=1">
              Connect Planning Center
            </a>
            <p style={{ fontSize: 12, marginTop: 14 }}>
              No Planning Center login? Ask your leader to add you — then sign out and
              back in.
            </p>
          </div>
        )}

        <Section eyebrow="Word" title="For such a time.">
          <div className="verse">
            <p>“{VERSE.text}”</p>
            <div className="ref">{VERSE.ref}</div>
          </div>
        </Section>

        <Section eyebrow="Give Out Loud" title="Give Here.">
          <div className="givecard">
            <p>
              Generosity isn't quiet in this house. We give like God is trustworthy —
              because He is.
            </p>
            <a className="givebtn" href={LINKS.give} target="_blank" rel="noreferrer">
              💛 Give now
            </a>
            <div className="givenote">
              Apple Pay · card · bank — about 30 seconds, secure through Church Center.
            </div>
          </div>
        </Section>

        {!unlinked && (
          <>
            <Section eyebrow="This Season" title="What's next.">
              <button className="featured ship" onClick={() => go("ship-happens")}>
                <div className="feyebrow">New series · from the book by {SERIES.source.author}</div>
                <h3>
                  {SERIES.title.replace(/\.$/, "")}
                  <span className="dot">.</span>
                </h3>
                <p>{SERIES.subtitle} Ten ships — find out which one you're on.</p>
                <span className="go">Open the field guide →</span>
              </button>
            </Section>

            <Section
              eyebrow="Discipleship & Development"
              title="myTRiBE+."
              keepCase
              sub="Your journey from first Sunday to leading the room — SERVE, PRAY, GIVE. Finish the path and join a TRiBE+."
            >
              <Row
                icon="🧭"
                name="Start your journey"
                desc="Classes, checkpoints, and the There Is More trails."
                onGo={() => go("tribe")}
              />
            </Section>
          </>
        )}

        <Section
          eyebrow="Latest Message"
          title="Press play."
          sub="The newest sermon plays right here. Older messages queue up after it."
        >
          <div className="player">
            <iframe
              src={`https://www.youtube.com/embed/videoseries?list=${SERMON_PLAYLIST_ID}&rel=0`}
              title="CLLXTV latest message"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </Section>

        {!unlinked && (
          <>
        <Section
          eyebrow="Inside The Room"
          title="Go deeper."
          sub="Full pages, right inside the hub."
        >
          <Row
            icon="🧬"
            name="Culture Codes"
            desc="Who we are, what we double down on — all 14 codes in one spot."
            onGo={() => go("culture")}
          />
          <Row
            icon="🙏"
            name="Prayer & Testimony Wall"
            desc="Post a request, share what God did, or send a private note to leadership."
            onGo={() => go("wall")}
          />
          <Row
            icon="🙌"
            name="Join the Dream Team"
            desc="Meet the teams and raise your hand."
            onGo={() => go("dreamteam")}
          />
          <Row
            icon="🗓️"
            name="myTRiBE Calendar"
            desc="Tap through July, August, and September — services, Prayer Night, the music drop, and more."
            onGo={() => go("calendar")}
          />
          <Row
            icon="🎵"
            name="CLLXTV Music"
            desc="Song of the day and Sunday worship sets."
            onGo={() => go("worship")}
          />
          {userRank >= rankOf("leader") && (
            <Row
              icon="📈"
              name="LEADERSHiP+"
              desc="Build your lid — the shelf, the Roundtable, and a real check-in."
              onGo={() => go("leadershipplus")}
            />
          )}
        </Section>

        <Section
          eyebrow="GroupMe"
          title="Get in the chat."
          sub="Tap a group to open it in the GroupMe app. Locked rooms unlock as you step into leadership."
        >
          {GROUPME.map((g) => (
            <LockedCard key={g.name} item={g} userRank={userRank} icon />
          ))}
        </Section>

        <Section
          eyebrow="Your Workspace"
          title="The library."
          sub="Resources for every team — training, brand files, run of show, and the documents that keep the house in order."
        >
          <Row
            icon="🚀"
            name="Onboarding"
            desc="New to the Dream Team? Your first-week page — five steps and the library."
            onGo={() => go("onboard")}
          />
          <Row
            icon="📜"
            name="SOPs & Playbooks"
            desc="How we run Sundays — your team's checklists and how-tos."
            onGo={() => go("ops")}
          />
          {userRank >= rankOf("director") && (
            <Row
              icon="🗂️"
              name="Org Chart"
              desc="SATX Campus — who carries what, tap to expand each branch."
              onGo={() => go("org")}
            />
          )}
          {LIBRARY.map((f) => (
            <LockedCard key={f.name} item={f} userRank={userRank} icon />
          ))}
        </Section>
          </>
        )}

        <Section
          eyebrow="Every Member"
          title="The essentials."
          sub="Giving and forms open Church Center so your payment and personal info stay behind a real login."
        >
          <a className="card" href={LINKS.give} target="_blank" rel="noreferrer">
            <div className="name">
              <span>Give</span>
              <span className="arrow">→</span>
            </div>
            <div className="desc">Single gift or recurring. Simple and secure.</div>
          </a>
          <a className="card" href={LINKS.forms} target="_blank" rel="noreferrer">
            <div className="name">
              <span>Forms & sign-ups</span>
              <span className="arrow">→</span>
            </div>
            <div className="desc">Serve interest, events, next steps.</div>
          </a>
        </Section>

        <div className="footer">
          <strong>{SITE.name}</strong> · {SITE.gatherTime}
          <br />
          {SITE.campus}
          <br />
          {SITE.tagline}
          <br />
          <button className="lockout" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
